import React, { useRef } from "react"
import PropTypes from "prop-types"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

const options = { appearance: { theme: "flat" } }

const PLATFORM_ACCOUNT = "platform"

// One Stripe instance per account, reused across mounts. Calling loadStripe again for the same
// account would build a second Stripe object, and Elements created by one instance cannot be
// passed to another.
const stripePromises = new Map()

const getStripePromise = (stripeAccountId) => {
  const cacheKey = stripeAccountId || PLATFORM_ACCOUNT

  if (!stripePromises.has(cacheKey)) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    stripePromises.set(cacheKey, loadStripe(publishableKey, stripeAccountId ? { stripeAccount: stripeAccountId } : undefined))
  }

  return stripePromises.get(cacheKey)
}

/**
 * Elements provider scoped to the account that will actually be charged.
 *
 * Guides on Direct Charges (Express) take payment on their own connected account, so every Stripe
 * operation in the subtree — creating a payment method, confirming a PaymentIntent — has to run on
 * a Stripe instance built with `stripeAccount`. Guides still on Destination Charges fall through to
 * the platform account.
 *
 * @param {object} props
 * @param {object} props.stripeAccountData - Guide's `stripe_account_data` from the API
 * @param {boolean} props.isReady - False while `stripeAccountData` is still loading
 * @param {React.ReactNode} props.children
 */
export default function DynamicStripeWrapper({ stripeAccountData, isReady = true, children }) {
  const stripeAccountId = stripeAccountData?.use_direct_charge ? stripeAccountData?.stripe_account_id : null

  // Elements does not support swapping its `stripe` prop: a change remounts the card fields and
  // wipes whatever the payer has typed. So resolve the account once, on the first render where the
  // answer is known, and hold it. Until then Elements gets null, which it explicitly supports —
  // children render immediately and `useStripe()` returns null, which every payment handler
  // already guards against.
  const stripePromiseRef = useRef(null)
  if (isReady && !stripePromiseRef.current) stripePromiseRef.current = getStripePromise(stripeAccountId)

  return (
    <Elements stripe={stripePromiseRef.current} options={options}>
      {children}
    </Elements>
  )
}

DynamicStripeWrapper.propTypes = {
  stripeAccountData: PropTypes.shape({
    stripe_account_id: PropTypes.string,
    use_direct_charge: PropTypes.bool,
    payment_flow_version: PropTypes.string
  }),
  isReady: PropTypes.bool,
  children: PropTypes.node.isRequired
}
